import { syncKey, syncKeyWithMerge, safeSetItem } from './supabase'
import { upsertPedidoRemoto, hardDeletePedidoRemoto } from './supabase-pedidos'
import { registrarEdicao, registrarCriacao, registrarExclusao, registrarRestauracao } from './auditoria'

// Migração automática de status legados
const STATUS_LEGADO_MAP = { A_FABRICAR: 'CADASTRADO', FABRICADO: 'EXPEDICAO', POS_VENDA: 'ENTREGUE' }
function _migrateStatuses() {
  if (localStorage.getItem('mini_migration_status_v2')) return
  try {
    const raw = localStorage.getItem('mini_pedidos')
    if (!raw) {
      localStorage.setItem('mini_migration_status_v2','1')
      return
    }
    const pedidos = JSON.parse(raw)
    let changed = false
    pedidos.forEach(p => { if (STATUS_LEGADO_MAP[p.status]) { p.status = STATUS_LEGADO_MAP[p.status]; changed = true } })
    if (changed) safeSetItem('mini_pedidos', JSON.stringify(pedidos))
    // Flag só é setada se tudo acima correu bem. Antes ficava FORA do try,
    // então um JSON corrompido ou um safeSetItem que estourasse marcava a
    // migração como feita e os pedidos ficavam com status legado pra sempre.
    localStorage.setItem('mini_migration_status_v2','1')
  } catch (e) {
    try { localStorage.setItem('mini_last_sync_error', '_migrateStatuses: ' + String(e?.message || e)) } catch {}
    // NÃO seta a flag — próximo boot tenta de novo.
  }
}
_migrateStatuses()

const PEDIDOS_KEY         = 'mini_pedidos'
const CONFIG_KEY          = 'mini_config'
const TAGS_KEY            = 'mini_tags'
const BLING_CONTATOS_KEY  = 'bling_contatos'
const BLING_CONTATOS_META = 'bling_contatos_meta'
const BLING_HISTORICO_KEY  = 'bling_historico'
const BLING_HISTORICO_META = 'bling_historico_meta'

// Versão RAW — inclui pedidos deletados (usada internamente pra achar
// pra restaurar/apagar definitivo).
export function getPedidosRaw() {
  try {
    const raw = localStorage.getItem(PEDIDOS_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

// Padrão: só pedidos "vivos" (sem deletadoEm). É o que todas as telas
// consomem — Pedidos, Logística, Kanban, Dashboards etc. Vendedora não
// vê os deletados.
export function getPedidos() {
  return getPedidosRaw().filter(p => !p.deletadoEm)
}

// Lixeira: só admin/gerente vê via /lixeira.
export function getPedidosDeletados() {
  return getPedidosRaw()
    .filter(p => p.deletadoEm)
    .sort((a, b) => (b.deletadoEm || '').localeCompare(a.deletadoEm || ''))
}

// Salva pedido registrando o histórico (edição ou criação) automaticamente.
// Compara com a versão anterior no localStorage pra saber o que mudou.
export function savePedido(pedido) {
  const pedidos = getPedidosRaw()  // inclui deletados pra achar existente
  const idx = pedidos.findIndex(p => p.id === pedido.id)
  let pedidoFinal
  if (idx >= 0) {
    // Edição — compara com o anterior e registra mudanças
    pedidoFinal = registrarEdicao(pedidos[idx], pedido)
    pedidos[idx] = pedidoFinal
  } else {
    // Criação — verifica colisão de número (Camada 2). O NewOrder já
    // chamou getNextNumeroPedido, mas o histórico Bling pode ter
    // sincronizado entre o clique de "Novo pedido" e o "Salvar", ou
    // outro dispositivo pode ter cadastrado em paralelo. Se colidir,
    // regenera — melhor um número pulado que colisão silenciosa.
    const extrair = numero => {
      const partes = String(numero || '').split('-')
      return parseInt(partes[partes.length - 1] || '0', 10)
    }
    const usados = new Set([
      ...pedidos.map(p => extrair(p.numero)).filter(n => n > 0),
      ...getBlingHistorico().map(p => extrair(p.numeroLoja)).filter(n => n > 0),
    ])
    if (usados.has(extrair(pedido.numero))) {
      pedido = { ...pedido, numero: getNextNumeroPedido() }
    }
    pedidoFinal = registrarCriacao(pedido)
    pedidos.unshift(pedidoFinal)
  }
  safeSetItem(PEDIDOS_KEY, JSON.stringify(pedidos))
  // Upsert POR ID na tabela `pedidos` do Supabase. Operação atômica em
  // UMA linha — impossível destruir pedidos de outros devices. Substitui
  // definitivamente o modelo antigo (reescrita de array JSON) que causou
  // 3 regressões em 7 dias (22/10, 29/10, 30/10).
  upsertPedidoRemoto(pedidoFinal)
  return pedidoFinal
}

// True se existe pedido no bling_historico com o mesmo numeroLoja.
// Usado no modal de excluir pra alertar: deletar o local não cancela
// o Bling, e ele vai "reaparecer" como pedido Bling puro sem os
// campos locais (valorPendenteEntrega, obs, split de pagamento).
export function pedidoTemEspelhoBling(numero) {
  if (!numero) return false
  return getBlingHistorico().some(p => p.numeroLoja === numero)
}

// Camada 3: detecta pedidos com número duplicado (local↔Bling ou
// local↔local) — mas SÓ quando o cliente é diferente. Todo pedido local
// enviado pro Bling volta como pedido Bling com mesmo número (espelho da
// integração); isso NÃO é colisão real, é a arquitetura normal. Colisão
// real é quando 2 pessoas diferentes cadastraram pedidos com o mesmo
// número (por race entre local e Bling ou entre dispositivos locais).
// Roda no boot, no mini:resynced e a cada 60s; se retornar não-vazio,
// o ColisaoBanner mostra alerta em cima da navbar pra admin/gerente
// abrir /diagnostico e resolver via botão "Renumerar".
function normalizarNomeCliente(nome) {
  return (nome || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')  // remove acentos
    .replace(/\s+/g, ' ')
    .trim()
}
// Compara nomes: mesmo cliente se um contém o outro OU se primeiro+último
// batem. Pega variações comuns: "Patricia Avila" vs "Patricia Avila Cintra"
// (Bling costuma ter nome completo), "José da Silva" vs "Jose Silva",
// "MARIA JOAO" vs "Maria Joao Santos". Não pega gente com mesmo primeiro
// nome mas família diferente (Maria Silva vs Maria Souza → diferente, ok).
function mesmoCliente(a, b) {
  if (!a || !b) return false
  if (a === b) return true
  if (a.includes(b) || b.includes(a)) return true
  const tok = s => s.split(' ').filter(Boolean)
  const ta = tok(a), tb = tok(b)
  if (ta.length < 2 || tb.length < 2) return false
  // Primeiro E último nome iguais
  return ta[0] === tb[0] && ta[ta.length - 1] === tb[tb.length - 1]
}
export function detectarColisoesNumeros() {
  const extrair = numero => {
    const partes = String(numero || '').split('-')
    return parseInt(partes[partes.length - 1] || '0', 10)
  }
  const locaisPorNumero = new Map()
  getPedidos().forEach(p => {
    const n = extrair(p.numero)
    if (!n) return
    if (!locaisPorNumero.has(n)) locaisPorNumero.set(n, [])
    locaisPorNumero.get(n).push({
      id: p.id,
      numero: p.numero,
      cliente: p.cliente?.nome || '—',
      _nomeNorm: normalizarNomeCliente(p.cliente?.nome),
    })
  })
  // Blings ocultos não devem contar como colisão — o admin já resolveu
  // o caso ocultando aquele pedido Bling. Se o Bling é oculto, ele já não
  // aparece na Logística/Kanban, então não tem nada "escondido" pra alertar.
  const ocultos = getBlingOcultos()
  const blingPorNumero = new Map()
  getBlingHistorico().forEach(p => {
    const n = extrair(p.numeroLoja)
    if (!n) return
    const id = `bling-${p.id}`
    if (ocultos.has(id)) return
    // Suporta múltiplos Blings com mesmo número (raro, mas visto no caso
    // do André 2026-0009 que tinha 2 pedidos de teste com mesmo número).
    if (!blingPorNumero.has(n)) blingPorNumero.set(n, [])
    blingPorNumero.get(n).push({
      id,
      numero: p.numeroLoja,
      cliente: p.contato?.nome || '—',
      _nomeNorm: normalizarNomeCliente(p.contato?.nome),
    })
  })
  const colisoes = []
  // Local × Bling — filtra o espelho (mesmo cliente). Só reporta se
  // NENHUM local com esse número tiver o mesmo cliente que o Bling.
  for (const [n, blings] of blingPorNumero) {
    const locais = locaisPorNumero.get(n)
    if (!locais) continue
    for (const bling of blings) {
      const temEspelho = locais.some(l => mesmoCliente(l._nomeNorm, bling._nomeNorm))
      if (temEspelho) continue
      colisoes.push({
        numero: n,
        tipo: 'local-bling',
        local: locais.map(({ _nomeNorm, ...rest }) => rest),
        bling: (({ _nomeNorm, ...rest }) => rest)(bling),
      })
    }
  }
  // Local × Local — 2 pedidos LOCAIS com mesmo número. Se forem clientes
  // diferentes é colisão real; se forem o mesmo cliente é só duplicata
  // acidental (rara mas não requer renumerar).
  for (const [n, arr] of locaisPorNumero) {
    if (arr.length < 2) continue
    const nomesUnicos = new Set(arr.map(p => p._nomeNorm).filter(Boolean))
    if (nomesUnicos.size < 2) continue
    colisoes.push({
      numero: n,
      tipo: 'local-local',
      pedidos: arr.map(({ _nomeNorm, ...rest }) => rest),
    })
  }
  return colisoes
}

// Soft-delete: marca deletadoEm/deletadoPor em vez de apagar. Fica na
// lixeira pra recuperação. Gerente/admin acessa via /lixeira.
export function deletePedido(id, motivo) {
  const pedidos = getPedidosRaw()
  const idx = pedidos.findIndex(p => p.id === id)
  if (idx < 0) return
  const user = (() => { try { return JSON.parse(localStorage.getItem('mini_current_user') || 'null') } catch { return null } })()
  const comHistorico = registrarExclusao(pedidos[idx], motivo)
  pedidos[idx] = {
    ...comHistorico,
    deletadoEm:  new Date().toISOString(),
    deletadoPor: user?.username || 'desconhecido',
    motivoExclusao: motivo || '',
  }
  safeSetItem(PEDIDOS_KEY, JSON.stringify(pedidos))
  upsertPedidoRemoto(pedidos[idx])
}

// Restaura pedido da lixeira. Só gerente/admin chama.
export function restaurarPedido(id) {
  const pedidos = getPedidosRaw()
  const idx = pedidos.findIndex(p => p.id === id)
  if (idx < 0) return
  const comHistorico = registrarRestauracao(pedidos[idx])
  const { deletadoEm, deletadoPor, motivoExclusao, ...limpo } = comHistorico
  pedidos[idx] = limpo
  safeSetItem(PEDIDOS_KEY, JSON.stringify(pedidos))
  upsertPedidoRemoto(limpo)
}

// Apaga DEFINITIVAMENTE (só admin, só via lixeira). Sem volta.
// Agora é um DELETE por id na tabela — não precisa mais do `idsToRemove`
// do merge, porque não há mais merge.
export function apagarPedidoDefinitivo(id) {
  const pedidos = getPedidosRaw().filter(p => p.id !== id)
  safeSetItem(PEDIDOS_KEY, JSON.stringify(pedidos))
  hardDeletePedidoRemoto(id)
}

export function updateStatus(id, status) {
  const pedidos = getPedidosRaw()
  const idx = pedidos.findIndex(p => p.id === id)
  if (idx >= 0) {
    // Muda o status via savePedido pra que o histórico registre a mudança
    savePedido({ ...pedidos[idx], status })
  }
}

// Retorna o próximo número livre no formato YYYY-XXXX considerando:
//   1. Pedidos locais existentes (mini_pedidos, incluindo deletados)
//   2. Pedidos do Bling que têm numeroLoja no mesmo formato
// Sem isso, se o Bling gerar "2026-0170" e depois uma vendedora cadastrar
// local, o contador ignora o Bling e reatribui "2026-0170" — a dedup
// esconde o pedido do Bling e o cliente reclama. Aconteceu 2x (23/07 e
// caso anterior). Ver também detectarColisoesNumeros() e a Camada 2 do
// savePedido, que fecham os casos que este contador não pega
// (histórico Bling desatualizado, cadastros simultâneos).
export function getNextNumeroPedido() {
  const ano = new Date().getFullYear()
  const extrair = numero => {
    const partes = String(numero || '').split('-')
    return parseInt(partes[partes.length - 1] || '0', 10)
  }
  // Locais: usa getPedidosRaw pra incluir soft-deletados — número
  // deletado não deve reciclar até apagar definitivo.
  const numerosLocais = getPedidosRaw()
    .map(p => extrair(p.numero))
    .filter(n => !isNaN(n) && n > 0)
  // Bling: usa numeroLoja (formato 2026-0170) — se não vier, cai como
  // "BL-4459" no useOrders e não colide com locais.
  const numerosBling = getBlingHistorico()
    .map(p => extrair(p.numeroLoja))
    .filter(n => !isNaN(n) && n > 0)
  const max = Math.max(0, ...numerosLocais, ...numerosBling)
  return `${ano}-${String(max + 1).padStart(4, '0')}`
}

export function getConfig() {
  try {
    const raw = localStorage.getItem(CONFIG_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

export function saveConfig(config) {
  localStorage.setItem(CONFIG_KEY, JSON.stringify(config))
  syncKey(CONFIG_KEY)
}

// ── Tags ─────────────────────────────────────────────────────────────────────
export function getTags() {
  try {
    const raw = localStorage.getItem(TAGS_KEY)
    return raw ? JSON.parse(raw) : []
  } catch { return [] }
}

export function saveTag(tag) {
  const tags = getTags()
  const idx  = tags.findIndex(t => t.id === tag.id)
  if (idx >= 0) tags[idx] = tag
  else tags.push(tag)
  safeSetItem(TAGS_KEY, JSON.stringify(tags))
  syncKeyWithMerge(TAGS_KEY)
}

export function deleteTag(id) {
  const tags = getTags().filter(t => t.id !== id)
  safeSetItem(TAGS_KEY, JSON.stringify(tags))
  // Delete real: id removido local E remoto (preserva merge nas outras tags).
  syncKeyWithMerge(TAGS_KEY, new Set([id]))
  // Remove tag de TODOS os pedidos (inclusive os da lixeira). Agora
  // propagamos a mudança um por um pela tabela — só os pedidos que
  // tinham essa tag sofrem UPDATE, os outros ficam intactos. Antes era
  // reescrita do array inteiro, que abria caminho pra corromper tudo.
  const pedidosRaw = getPedidosRaw()
  const alterados = []
  const novoArray = pedidosRaw.map(p => {
    const tinha = (p.tags || []).includes(id)
    if (!tinha) return p
    const novo = { ...p, tags: p.tags.filter(tid => tid !== id) }
    alterados.push(novo)
    return novo
  })
  safeSetItem(PEDIDOS_KEY, JSON.stringify(novoArray))
  alterados.forEach(p => upsertPedidoRemoto(p))
}

// ── Contatos importados do Bling ──────────────────────────────────────────────
export function getBlingContatos() {
  try { return JSON.parse(localStorage.getItem(BLING_CONTATOS_KEY) || '[]') } catch { return [] }
}

export function saveBlingContatos(contatos) {
  safeSetItem(BLING_CONTATOS_KEY, JSON.stringify(contatos))
  safeSetItem(BLING_CONTATOS_META, JSON.stringify({ importadoEm: Date.now(), total: contatos.length }))
}

export function getBlingContatosMeta() {
  try { return JSON.parse(localStorage.getItem(BLING_CONTATOS_META) || 'null') } catch { return null }
}

export function clearBlingContatos() {
  localStorage.removeItem(BLING_CONTATOS_KEY)
  localStorage.removeItem(BLING_CONTATOS_META)
}

// Mescla contatos enriquecidos (com endereco.geral) no store existente
export function updateBlingContatosEnriquecidos(enriquecidos) {
  const existing = getBlingContatos()
  const map = new Map(existing.map(c => [String(c.id), c]))
  enriquecidos.forEach(c => {
    map.set(String(c.id), { ...(map.get(String(c.id)) || {}), ...c })
  })
  safeSetItem(BLING_CONTATOS_KEY, JSON.stringify([...map.values()]))
}

// ── Histórico de pedidos importados do Bling ──────────────────────────────────
export function getBlingHistorico() {
  try { return JSON.parse(localStorage.getItem(BLING_HISTORICO_KEY) || '[]') } catch { return [] }
}

export function saveBlingHistorico(pedidos) {
  safeSetItem(BLING_HISTORICO_KEY, JSON.stringify(pedidos))
  safeSetItem(BLING_HISTORICO_META, JSON.stringify({ importadoEm: Date.now(), total: pedidos.length }))
}

export function getBlingHistoricoMeta() {
  try { return JSON.parse(localStorage.getItem(BLING_HISTORICO_META) || 'null') } catch { return null }
}

export function clearBlingHistorico() {
  localStorage.removeItem(BLING_HISTORICO_KEY)
  localStorage.removeItem(BLING_HISTORICO_META)
  localStorage.removeItem(BLING_DETALHES_KEY)
  localStorage.removeItem(BLING_LOGISTICA_KEY)
  localStorage.removeItem(BLING_STATUS_KEY)
  localStorage.removeItem(BLING_OCULTOS_KEY)
}

export function clearBlingDetalhes() {
  localStorage.removeItem(BLING_DETALHES_KEY)
}

// ── Bling: status e logística dos pedidos ativos ──────────────────────────────
const BLING_STATUS_KEY    = 'bling_pedidos_status'
const BLING_LOGISTICA_KEY = 'bling_pedidos_logistica'
const BLING_OCULTOS_KEY   = 'bling_pedidos_ocultos'
const BLING_DETALHES_KEY  = 'bling_pedidos_detalhes'

function mapBlingStatus(situacaoId) {
  if (situacaoId === 9)  return 'POS_VENDA'
  if (situacaoId === 15) return 'FABRICANDO'
  return 'A_FABRICAR'
}

export function getBlingStatusOverrides() {
  try { return JSON.parse(localStorage.getItem(BLING_STATUS_KEY) || '{}') } catch { return {} }
}
export function setBlingStatusOverride(id, status) {
  const o = getBlingStatusOverrides()
  o[id] = status
  safeSetItem(BLING_STATUS_KEY, JSON.stringify(o))
  syncKey(BLING_STATUS_KEY)
}

export function getBlingLogisticaData() {
  try { return JSON.parse(localStorage.getItem(BLING_LOGISTICA_KEY) || '{}') } catch { return {} }
}
export function saveBlingLogisticaItem(id, data) {
  const o = getBlingLogisticaData()
  o[id] = { ...o[id], ...data }
  safeSetItem(BLING_LOGISTICA_KEY, JSON.stringify(o))
  syncKey(BLING_LOGISTICA_KEY)
}

export function getBlingPedidosDetalhes() {
  try { return JSON.parse(localStorage.getItem(BLING_DETALHES_KEY) || '{}') } catch { return {} }
}

export function saveBlingPedidoDetalhe(id, data) {
  const o = getBlingPedidosDetalhes()
  o[String(id)] = { ...o[String(id)], ...data }
  safeSetItem(BLING_DETALHES_KEY, JSON.stringify(o))
}

// Salva todos de uma vez — evita O(n²) de parse+stringify por pedido
export function saveBlingPedidosDetalhes(lista) {
  const o = getBlingPedidosDetalhes()
  lista.forEach(({ id, data }) => {
    o[String(id)] = { ...o[String(id)], ...data }
  })
  safeSetItem(BLING_DETALHES_KEY, JSON.stringify(o))
}

export function getBlingOcultos() {
  try { return new Set(JSON.parse(localStorage.getItem(BLING_OCULTOS_KEY) || '[]')) } catch { return new Set() }
}

export function hideBlingPedido(id) {
  const s = getBlingOcultos()
  s.add(String(id))
  safeSetItem(BLING_OCULTOS_KEY, JSON.stringify([...s]))
  // Array de ids string — merge preserva o que outros devices já ocultaram.
  syncKeyWithMerge(BLING_OCULTOS_KEY)
}

export function unhideBlingPedido(id) {
  const s = getBlingOcultos()
  const strId = String(id)
  s.delete(strId)
  safeSetItem(BLING_OCULTOS_KEY, JSON.stringify([...s]))
  // Remoção explícita: id também deve sumir do remoto, não voltar via merge.
  syncKeyWithMerge(BLING_OCULTOS_KEY, new Set([strId]))
}

// ── Marketplace: status de pagamento por pedido ───────────────────────────────
// Map { pedidoId → { pago: bool, dataPagamento: 'YYYY-MM-DD' } }
const MARKETPLACE_PAG_KEY = 'mini_marketplace_pagamentos'

export function getMarketplacePagamentos() {
  try { return JSON.parse(localStorage.getItem(MARKETPLACE_PAG_KEY) || '{}') }
  catch { return {} }
}

export function setMarketplacePago(pedidoId, pago, dataPagamento, remessaId) {
  const map = getMarketplacePagamentos()
  if (pago) {
    map[pedidoId] = {
      pago: true,
      dataPagamento: dataPagamento || new Date().toISOString().split('T')[0],
      remessaId: remessaId || null,
    }
  } else {
    delete map[pedidoId]
  }
  safeSetItem(MARKETPLACE_PAG_KEY, JSON.stringify(map))
  syncKey(MARKETPLACE_PAG_KEY)
}

// ── Marketplace: remessas de pagamento em lote ────────────────────────────────
// Cada remessa agrupa vários pedidos entregues do mesmo parceiro pagos juntos.
const MARKETPLACE_REMESSAS_KEY = 'mini_marketplace_remessas'

export function getMarketplaceRemessas() {
  try { return JSON.parse(localStorage.getItem(MARKETPLACE_REMESSAS_KEY) || '[]') }
  catch { return [] }
}

export function saveMarketplaceRemessa(remessa) {
  const lista = getMarketplaceRemessas()
  const idx = lista.findIndex(r => r.id === remessa.id)
  if (idx >= 0) lista[idx] = remessa
  else lista.push(remessa)
  safeSetItem(MARKETPLACE_REMESSAS_KEY, JSON.stringify(lista))
  syncKeyWithMerge(MARKETPLACE_REMESSAS_KEY)
}

export function deleteMarketplaceRemessa(id) {
  const lista = getMarketplaceRemessas().filter(r => r.id !== id)
  safeSetItem(MARKETPLACE_REMESSAS_KEY, JSON.stringify(lista))
  syncKeyWithMerge(MARKETPLACE_REMESSAS_KEY, new Set([id]))
}

const CORTE_STATUS = '2026-04-20'

function statusPorPrazo(prazo) {
  if (!prazo || prazo === '0000-00-00') return 'CADASTRADO'
  return prazo >= CORTE_STATUS ? 'CADASTRADO' : 'ENTREGUE'
}

export function getPedidosBlingAtivos() {
  const overrides  = getBlingStatusOverrides()
  const logistica  = getBlingLogisticaData()
  const detalhes   = getBlingPedidosDetalhes()
  const ocultos    = getBlingOcultos()
  const IGNORAR    = new Set([12, 0])   // cancelado, rascunho

  // Mapa de contatos por ID para lookup O(1) — já têm endereco.geral + celular
  const contatosMap = new Map(
    getBlingContatos().map(c => [String(c.id), c])
  )

  return getBlingHistorico()
    .filter(p => {
      if (IGNORAR.has(p.situacao?.id)) return false
      if (ocultos.has(`bling-${p.id}`)) return false
      return true
    })
    .map(p => {
      const id           = `bling-${p.id}`
      const log          = logistica[id] || {}
      const prazoEfetivo = log.prazoOverride || p.dataPrevista
      const status       = overrides[id] ?? statusPorPrazo(prazoEfetivo)
      const det          = detalhes[String(p.id)] || {}

      // Contato: cruza com store de contatos (tem endereço completo + telefone)
      const contatoId = String(det.contato?.id || p.contato?.id || '')
      const cont      = contatosMap.get(contatoId) || {}

      // Endereço — merge campo a campo das fontes do Bling API v3 em ordem
      // de prioridade. Antes a gente pegava "a primeira fonte com qualquer
      // campo", mas isso quebrava quando `det.transporte.enderecoEntrega`
      // tinha SÓ cep (sem endereço/cidade): a Logística ficava com endereço
      // vazio e detectarCidadeAba caía em "OUTRAS", sumindo da aba regional.
      // Agora: pra cada campo, pega o primeiro valor não-vazio das fontes.
      const fontesEndereco = [
        det.transporte?.enderecoEntrega,          // entrega no pedido detalhe
        det.contato?.endereco?.entrega,           // endereço entrega do contato
        det.contato?.endereco?.geral,             // endereço geral do contato
        det.contato?.endereco?.cobranca,          // endereço cobrança como fallback
        cont.endereco?.entrega,                   // store de contatos (Fase 3)
        cont.endereco?.geral,                     // store de contatos (Fase 3)
      ].filter(e => e && typeof e === 'object')
      const pickField = k => {
        for (const f of fontesEndereco) if (f[k]) return f[k]
        return ''
      }
      const end = {
        endereco:    pickField('endereco'),
        numero:      pickField('numero'),
        complemento: pickField('complemento'),
        bairro:      pickField('bairro'),
        municipio:   pickField('municipio') || pickField('cidade'),
        cep:         pickField('cep'),
        uf:          pickField('uf'),
      }
      const enderecoFull = [
        end.endereco, end.numero, end.complemento,
        end.bairro, end.municipio,
      ].filter(Boolean).join(', ')

      // Contato/endereço/resumo: edição livre na Logística vence o original
      const contatoOriginal = p.contato?.celular || p.contato?.telefone ||
                              det.contato?.celular || det.contato?.telefone ||
                              cont.celular || cont.telefone || ''
      const resumoOriginal  = (det.itens || []).map(i => {
        const qtd = i.quantidade > 1 ? ` ×${i.quantidade}` : ''
        return (i.descricao || i.codigo || 'Item') + qtd
      }).join(' · ')
      return {
        id,
        numero:     p.numeroLoja || `BL-${p.numero}`,
        dataVenda:  p.data       || null,
        prazo:      prazoEfetivo,
        valorTotal: Number(p.totalVenda ?? p.total ?? 0),
        status,
        origem:     'bling',
        cliente: {
          nome:     p.contato?.nome    || det.contato?.nome    || cont.nome || '—',
          contato:  log.contatoOverride || contatoOriginal,
          email:    det.contato?.email || cont.email || '',
          endereco: log.enderecoOverride || enderecoFull,
          bairro:   end.bairro    || '',
          cidade:   end.municipio || '',
          cep:      end.cep       || '',
          uf:       end.uf        || '',
        },
        moveis:  [],
        tags:    [],
        itens:   det.itens || [],
        resumo:  log.resumoOverride || resumoOriginal,
        vendedora:      det.vendedor?.nome           || p.vendedor?.nome || '',
        formaPagamento: det.formaPagamento?.descricao || '',
        // campos de logística salvos localmente
        horarioEntrega:       log.horarioEntrega       || '',
        confirmacaoEntrega:   log.confirmacaoEntrega   || '',
        enderecoEntrega:      log.enderecoEntrega       || '',
        observacoesLogistica: log.observacoesLogistica || '',
        atencao:              !!log.atencao,
        prazoOriginal:        log.prazoOriginal        || '',
        // Checkpoint da aba "A Receber"
        pagoEntrega:          !!log.pagoEntrega,
        // Tracking de compras terceirizadas (pé/base/metalon)
        terceirizadosEncomendados: log.terceirizadosEncomendados || [],
      }
    })
}

// ── Migração one-time: seta status por prazo em todos os pedidos ──────────────
const MIGRATION_PRAZO_KEY = 'mini_migration_prazo_status_v1'

export function migrarStatusesPorPrazo() {
  if (localStorage.getItem(MIGRATION_PRAZO_KEY)) return

  // Pedidos locais: aplica regra por prazo nos ATIVOS, preserva os
  // deletados intactos (não recalcula status na lixeira).
  const pedidos = getPedidosRaw().map(p =>
    p.deletadoEm ? p : { ...p, status: statusPorPrazo(p.prazo) }
  )
  safeSetItem(PEDIDOS_KEY, JSON.stringify(pedidos))

  // Bling: limpa overrides para que a regra nova seja aplicada automaticamente
  localStorage.removeItem(BLING_STATUS_KEY)

  localStorage.setItem(MIGRATION_PRAZO_KEY, '1')
}

const LOGISTICA_ORDEM_KEY = 'mini_logistica_ordem'
export function getLogisticaOrdem() {
  try { return JSON.parse(localStorage.getItem(LOGISTICA_ORDEM_KEY) || '[]') } catch { return [] }
}
export function saveLogisticaOrdem(ids) {
  localStorage.setItem(LOGISTICA_ORDEM_KEY, JSON.stringify(ids))
}

// ── Oportunidades Arco (leads de móveis planejados pra Arco Marcenaria) ──────
const OPORTUNIDADES_ARCO_KEY = 'mini_oportunidades_arco'

export function getOportunidadesArco() {
  try { return JSON.parse(localStorage.getItem(OPORTUNIDADES_ARCO_KEY) || '[]') } catch { return [] }
}

export function saveOportunidadeArco(o) {
  const lista = getOportunidadesArco()
  const idx   = lista.findIndex(x => x.id === o.id)
  if (idx >= 0) lista[idx] = o
  else lista.unshift(o)  // mais recente no topo
  safeSetItem(OPORTUNIDADES_ARCO_KEY, JSON.stringify(lista))
  syncKeyWithMerge(OPORTUNIDADES_ARCO_KEY)
}

export function deleteOportunidadeArco(id) {
  const lista = getOportunidadesArco().filter(x => x.id !== id)
  safeSetItem(OPORTUNIDADES_ARCO_KEY, JSON.stringify(lista))
  syncKeyWithMerge(OPORTUNIDADES_ARCO_KEY, new Set([id]))
}

// ── Paradas avulsas da Logística (tarefas da rotina por data) ────────────────
const PARADAS_LOGISTICA_KEY = 'mini_paradas_logistica'

export function getParadas() {
  try { return JSON.parse(localStorage.getItem(PARADAS_LOGISTICA_KEY) || '[]') } catch { return [] }
}

export function saveParada(p) {
  const lista = getParadas()
  const idx   = lista.findIndex(x => x.id === p.id)
  if (idx >= 0) lista[idx] = p
  else lista.push(p)
  localStorage.setItem(PARADAS_LOGISTICA_KEY, JSON.stringify(lista))
  syncKey(PARADAS_LOGISTICA_KEY)
}

export function deleteParada(id) {
  const lista = getParadas().filter(x => x.id !== id)
  localStorage.setItem(PARADAS_LOGISTICA_KEY, JSON.stringify(lista))
  syncKey(PARADAS_LOGISTICA_KEY)
}

export function toggleTagPedido(pedidoId, tagId) {
  const pedidos = getPedidosRaw()
  const idx     = pedidos.findIndex(p => p.id === pedidoId)
  if (idx < 0) return
  const tags    = pedidos[idx].tags || []
  pedidos[idx].tags = tags.includes(tagId)
    ? tags.filter(t => t !== tagId)
    : [...tags, tagId]
  safeSetItem(PEDIDOS_KEY, JSON.stringify(pedidos))
  // Upsert por id: só a linha desse pedido no Supabase sofre UPDATE.
  upsertPedidoRemoto(pedidos[idx])
  return pedidos[idx].tags
}
